/**
 * ALTIORA · Insights publics — vitrine anonymisée du meilleur contenu.
 *
 * Route publique (aucune auth) : /insights
 * Objectif : attirer prospects, journalistes et partenaires en montrant
 * l'intelligence éditoriale d'ALTIORA sans exposer de données clients.
 */
import { useEffect, useState, useMemo } from "react";
import { Sparkles, TrendingUp, Filter } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0a2540";
const CHAMPAGNE = "#c9a34e";
const INK = "#0a1a2c";
const SLATE = "#6b7c93";
const CANVAS = "#FBF6EA";

const CHANNEL_META = {
  linkedin:  { label: "LinkedIn",  emoji: "" },
  facebook:  { label: "Facebook",  emoji: "" },
  instagram: { label: "Instagram", emoji: "" },
  x:         { label: "X",         emoji: "" },
  youtube:   { label: "YouTube",   emoji: "" },
  tiktok:    { label: "TikTok",    emoji: "" },
  email:     { label: "Email",     emoji: "" },
  internal:  { label: "Article",   emoji: "" },
};

export default function PublicInsights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Injection des meta SEO / OpenGraph / Twitter card + JSON-LD
  useEffect(() => {
    const site = window.location.origin;
    const title = "Insights publics · ALTIORA — Intelligence décisionnelle pour conseillers CSF";
    const desc = "Le meilleur du contenu éditorial généré par ALTIORA — décrypté, noté par notre moteur d'intelligence, publié pour la communauté des conseillers en sécurité financière du Québec.";
    const image = `${site}/og/altiora-insights.png`;
    document.title = title;
    const set = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    set("description", desc);
    set("og:title", title, "property");
    set("og:description", desc, "property");
    set("og:type", "website", "property");
    set("og:url", `${site}/insights`, "property");
    set("og:image", image, "property");
    set("og:site_name", "ALTIORA", "property");
    set("twitter:card", "summary_large_image");
    set("twitter:title", title);
    set("twitter:description", desc);
    set("twitter:image", image);
    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `${site}/insights`;
    // JSON-LD CollectionPage
    let jsonld = document.getElementById("ld-insights");
    if (!jsonld) {
      jsonld = document.createElement("script");
      jsonld.type = "application/ld+json";
      jsonld.id = "ld-insights";
      document.head.appendChild(jsonld);
    }
    jsonld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "CollectionPage",
      name: title, description: desc, url: `${site}/insights`,
      publisher: { "@type": "Organization", name: "ALTIORA",
                     url: "https://altioracoop.com" },
    });
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND}/api/growth/public/insights?limit=48`)
         .then(r => setItems(r.data.items || []))
         .finally(() => setLoading(false));
  }, []);

  const channels = useMemo(() =>
    Array.from(new Set(items.map(i => i.channel))), [items]);

  const visible = filter === "all"
    ? items : items.filter(i => i.channel === filter);

  return (
    <div style={{ background: CANVAS, minHeight: "100vh", color: INK }}
          data-testid="public-insights-root">
      {/* Header */}
      <header className="border-b" style={{ borderColor: "#0a254022",
                                              background: NAVY, color: CANVAS }}>
        <div className="max-w-6xl mx-auto px-6 py-8">
          <a href="/" className="text-sm opacity-70 hover:opacity-100">
            ← Retour à ALTIORA
          </a>
          <div className="flex items-baseline gap-3 mt-4">
            <Sparkles size={22} style={{ color: CHAMPAGNE }} />
            <h1 className="text-4xl sm:text-5xl font-serif" style={{ color: CANVAS }}>
              Insights publics
            </h1>
          </div>
          <p className="mt-3 text-lg max-w-2xl opacity-80"
              style={{ color: CANVAS }}>
            Le meilleur du contenu éditorial généré par ALTIORA — décrypté,
            noté par notre moteur d'intelligence, et publié pour toute la
            communauté des conseillers en sécurité financière du Québec.
          </p>
          <p className="mt-2 text-xs opacity-60" style={{ color: CANVAS }}>
            Contenu anonymisé · aucune donnée client · score /100 par notre
            moteur d'évaluation IA
          </p>
        </div>
      </header>

      {/* Filter bar */}
      <div className="border-b" style={{ borderColor: "#0a254022" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center gap-2">
          <Filter size={14} style={{ color: SLATE }} />
          <button onClick={() => setFilter("all")}
                    data-testid="filter-all"
                    className="px-3 py-1 rounded-full text-xs"
                    style={{
                      background: filter === "all" ? NAVY : "transparent",
                      color: filter === "all" ? CANVAS : INK,
                      border: `1px solid ${filter === "all" ? NAVY : "#0a254033"}`,
                    }}>
            Tous ({items.length})
          </button>
          {channels.map(c => (
            <button key={c} onClick={() => setFilter(c)}
                     data-testid={`filter-${c}`}
                     className="px-3 py-1 rounded-full text-xs"
                     style={{
                       background: filter === c ? NAVY : "transparent",
                       color: filter === c ? CANVAS : INK,
                       border: `1px solid ${filter === c ? NAVY : "#0a254033"}`,
                     }}>
              {CHANNEL_META[c]?.label || c}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="text-center py-16" style={{ color: SLATE }}>
            Chargement des insights…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16" style={{ color: SLATE }}
                data-testid="insights-empty">
            <TrendingUp size={32} className="mx-auto mb-3 opacity-40" />
            <p>Aucun contenu à afficher pour ce filtre.</p>
            <p className="text-xs mt-2 opacity-70">
              Reviens bientôt — le catalogue grandit chaque semaine.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map(item => <InsightCard key={item.id} item={item} />)}
          </div>
        )}
      </main>

      {/* Footer CTA */}
      <footer className="border-t mt-10" style={{ borderColor: "#0a254022",
                                                     background: NAVY,
                                                     color: CANVAS }}>
        <div className="max-w-6xl mx-auto px-6 py-10 text-center">
          <h3 className="text-2xl font-serif mb-3" style={{ color: CANVAS }}>
            Vous êtes conseiller CSF ?
          </h3>
          <p className="mb-6 opacity-80 max-w-xl mx-auto">
            ALTIORA transforme vos connaissances en contenu monétisable —
            adapté par canal, noté sur 100, prêt à publier. En quelques clics.
          </p>
          <a href="/demo"
              data-testid="insights-cta-demo"
              className="inline-block px-6 py-3 rounded-full text-sm font-medium"
              style={{ background: CHAMPAGNE, color: NAVY }}>
            Demander une démo
          </a>
        </div>
      </footer>
    </div>
  );
}


function InsightCard({ item }) {
  const scoreColor = item.score >= 85 ? "#0a7f42"
                     : item.score >= 70 ? CHAMPAGNE : SLATE;
  const meta = CHANNEL_META[item.channel] || { label: item.channel };
  return (
    <article className="rounded-xl p-5 h-full flex flex-col"
              data-testid={`insight-card-${item.id}`}
              style={{ background: "#ffffff",
                       border: `1px solid #0a254011`,
                       boxShadow: "0 1px 3px #0a254008" }}>
      <div className="flex justify-between items-start mb-3">
        <span className="text-xs uppercase tracking-widest px-2 py-1 rounded"
                style={{ color: SLATE, background: "#0a254008" }}>
          {meta.label}
        </span>
        <div className="text-right">
          <div className="text-2xl font-bold leading-none"
                style={{ color: scoreColor }}>
            {item.score}
          </div>
          <div className="text-[10px] uppercase" style={{ color: SLATE }}>
            /100
          </div>
        </div>
      </div>
      <h3 className="font-serif text-lg mb-2 leading-snug"
           style={{ color: INK }}>
        {item.title}
      </h3>
      <p className="text-sm leading-relaxed flex-1"
          style={{ color: SLATE }}>
        {item.excerpt}
        {item.excerpt?.length >= 280 && "…"}
      </p>
      {!!item.hashtags?.length && (
        <div className="mt-3 flex flex-wrap gap-1">
          {item.hashtags.slice(0, 4).map((h, i) => (
            <span key={i} className="text-xs"
                    style={{ color: NAVY, opacity: 0.7 }}>{h}</span>
          ))}
        </div>
      )}
      {item.published_at && (
        <div className="text-xs mt-3 pt-3 border-t"
              style={{ color: SLATE, borderColor: "#0a254011" }}>
          Publié le {new Date(item.published_at).toLocaleDateString("fr-CA",
                        { year: "numeric", month: "long", day: "numeric" })}
        </div>
      )}
    </article>
  );
}
